/**
 * CADControls — turntable orbit/pan/zoom tuned for CAD, Z-up.
 *
 * Input map (deliberately SketchUp/Onshape-familiar):
 *   mouse    left-drag = Select marquee (claimed by the tool) · middle-drag or
 *            Alt+left orbit · right/shift-drag pan · wheel zoom-to-cursor
 *   trackpad two-finger scroll zoom-to-cursor · pinch zoom (ctrl+wheel on
 *            Chrome/Firefox; native gesturestart/gesturechange on Safari)
 *   touch    one finger orbit · two fingers pan + pinch zoom
 *   keys     F = fit all · double-click/tap = re-center target on model
 *
 * All state is target + spherical (theta around +Z, phi from the +Z pole,
 * radius). Zoom scales camera AND target about the cursor's world point, so
 * the model point under the cursor stays put — the single biggest "feels
 * right" factor in a CAD viewport.
 */

import * as THREE from 'three/webgpu';

/** Safari-only pinch events; not in lib.dom. */
interface WebKitGestureEvent extends UIEvent {
  readonly scale: number;
  readonly clientX: number;
  readonly clientY: number;
  preventDefault(): void;
}

const TWO_PI = Math.PI * 2;

/** A tool's claim on an in-progress pointer drag (see onDragStart). */
export interface DragHandler {
  move(e: PointerEvent): void;
  up(e: PointerEvent): void;
}

export class CADControls {
  readonly target = new THREE.Vector3(0, 0, 20);
  private theta = -Math.PI / 3;
  private phi = Math.PI / 3;
  private radius = 420;

  /** Fired whenever the camera moved and a re-render is needed. */
  onChange: (() => void) | null = null;
  /** Fired on double-click/tap with client coords (viewport recenters). */
  onDoublePress: ((clientX: number, clientY: number) => void) | null = null;
  /** Fired when the user asks to fit everything (F key). */
  onRequestFit: (() => void) | null = null;
  /**
   * Tool hook: called on primary-button/single-touch press BEFORE orbit
   * starts. Returning a handler claims the drag (push/pull, sketch tools);
   * returning null leaves the gesture to camera navigation.
   */
  onDragStart: ((e: PointerEvent) => DragHandler | null) | null = null;
  /** Fired on a click/tap that never turned into a drag (selection).
   * `additive` = shift held (multi-select). */
  onTap: ((clientX: number, clientY: number, additive: boolean) => void) | null = null;
  /** Right-click without dragging (right-DRAG stays pan): context menu. */
  onContextTap: ((clientX: number, clientY: number) => void) | null = null;
  private rightPress: { x: number; y: number; moved: boolean } | null = null;

  private activeDrag: DragHandler | null = null;
  private pressStart: { x: number; y: number; moved: boolean; shift: boolean } | null = null;

  private pointers = new Map<number, { x: number; y: number }>();
  private mouseMode: 'orbit' | 'pan' | null = null;
  private lastPinchDist = 0;
  private lastGestureScale = 1;
  private gestureActive = false;
  private lastTapTime = 0;
  private readonly abort = new AbortController();

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly dom: HTMLElement,
  ) {
    camera.up.set(0, 0, 1);
    const opts = { signal: this.abort.signal };
    const optsActive = { signal: this.abort.signal, passive: false };

    dom.addEventListener('pointerdown', this.onPointerDown, opts);
    dom.addEventListener('pointermove', this.onPointerMove, opts);
    dom.addEventListener('pointerup', this.onPointerUp, opts);
    dom.addEventListener('pointercancel', this.onPointerUp, opts);
    dom.addEventListener('wheel', this.onWheel, optsActive);
    // Suppress the native menu on the canvas only — panels keep theirs
    // (paste in the chat box, etc.).
    dom.addEventListener(
      'contextmenu',
      (e) => {
        if (!this.isUiChrome(e)) e.preventDefault();
      },
      opts,
    );
    dom.addEventListener('dblclick', this.onDblClick, opts);
    window.addEventListener('keydown', this.onKeyDown, opts);

    // Safari trackpad pinch. Cast: WebKit-only events aren't in lib.dom.
    for (const [name, fn] of [
      ['gesturestart', this.onGestureStart],
      ['gesturechange', this.onGestureChange],
      ['gestureend', this.onGestureEnd],
    ] as const) {
      dom.addEventListener(name, fn as EventListener, optsActive);
    }

    this.apply();
  }

  dispose(): void {
    this.abort.abort();
  }

  /** Write the current orbit state into the camera. */
  apply(): void {
    const sinPhi = Math.sin(this.phi);
    this.camera.position.set(
      this.target.x + this.radius * sinPhi * Math.cos(this.theta),
      this.target.y + this.radius * sinPhi * Math.sin(this.theta),
      this.target.z + this.radius * Math.cos(this.phi),
    );
    this.camera.lookAt(this.target);
    // Keep depth precision proportional to the working distance.
    this.camera.near = Math.max(this.radius / 100, 0.05);
    this.camera.far = this.radius * 200 + 2000;
    this.camera.updateProjectionMatrix();
    this.onChange?.();
  }

  fitBox(box: THREE.Box3): void {
    if (box.isEmpty()) return;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    this.target.copy(sphere.center);
    const vFov = (this.camera.fov * Math.PI) / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
    this.radius = (sphere.radius / Math.sin(Math.min(vFov, hFov) / 2)) * 1.08;
    this.apply();
  }

  /** Current orbit target (read-only copy). */
  getTarget(): THREE.Vector3 {
    return this.target.clone();
  }

  /** Re-center the orbit target without changing distance/orientation. */
  setTarget(point: THREE.Vector3): void {
    this.target.copy(point);
    this.apply();
  }

  /** Snapshot of the orbit state (for temporary view changes). */
  getState(): { target: [number, number, number]; theta: number; phi: number; radius: number } {
    return {
      target: [this.target.x, this.target.y, this.target.z],
      theta: this.theta,
      phi: this.phi,
      radius: this.radius,
    };
  }

  setState(s: { target: [number, number, number]; theta: number; phi: number; radius: number }): void {
    this.target.set(...s.target);
    this.theta = s.theta;
    this.phi = s.phi;
    this.radius = s.radius;
    this.apply();
  }

  /** Point the camera from a named direction (target/radius unchanged). */
  setOrientation(theta: number, phi: number): void {
    this.theta = theta;
    this.phi = Math.min(Math.PI - 0.02, Math.max(0.02, phi));
    this.apply();
  }

  // --- gestures -------------------------------------------------------------

  /**
   * Panels/toolbars live inside the viewport element, so their events bubble
   * here. Anything on UI chrome must never drive the camera or tap-selection
   * — otherwise clicking a panel input triggers a "tap", selection changes,
   * panels re-render, and the input the user is typing in gets destroyed.
   */
  private isUiChrome(e: Event): boolean {
    return (
      e.target instanceof Element &&
      e.target.closest('.nv-side, .nv-props, .nv-toolbar, .nv-hint, .nv-dim, .nv-menu, .nv-section, .nv-measure, .nv-sketch-dims, .nv-cheatsheet, .nv-drawing') !== null
    );
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (this.isUiChrome(e)) return;
    try {
      this.dom.setPointerCapture(e.pointerId);
    } catch {
      /* synthetic events have no active pointer to capture */
    }
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Offer primary presses to the active tool first. Shift+left is included
    // now — the Select marquee reads shift as "add to selection" (pan moved to
    // right-drag). Ctrl+left stays reserved for pan.
    const isPrimary =
      (e.pointerType === 'mouse' && e.button === 0 && !e.ctrlKey) ||
      (e.pointerType !== 'mouse' && this.pointers.size === 1);
    // Track the press for tap detection even when a tool claims the drag —
    // a claimed press that never moves is still a click (selection).
    // Shift-clicks count too (multi-select), though shift-DRAG stays pan.
    const tapEligible =
      e.pointerType === 'mouse'
        ? e.button === 0 && !e.ctrlKey
        : this.pointers.size === 1;
    this.pressStart = tapEligible
      ? { x: e.clientX, y: e.clientY, moved: false, shift: e.shiftKey }
      : null;
    this.rightPress =
      e.pointerType === 'mouse' && e.button === 2
        ? { x: e.clientX, y: e.clientY, moved: false }
        : null;
    if (isPrimary && !this.activeDrag && this.onDragStart) {
      this.activeDrag = this.onDragStart(e);
      if (this.activeDrag) return;
    }

    if (e.pointerType === 'mouse') {
      // Left-drag is the Select marquee (claimed above via onDragStart); orbit
      // moved to middle-drag and Alt+left so it stays reachable. Plain left
      // that WASN'T claimed (sketch inactive edge cases) still orbits.
      // Shift no longer forces pan (it's additive-select / a no-op modifier);
      // pan lives on right-drag and ctrl+left. So shift+left orbits like plain
      // left in Orbit/Push-Pull modes, matching the additive shift-click.
      this.mouseMode =
        e.button === 1 || (e.button === 0 && e.altKey)
          ? 'orbit'
          : e.button === 0 && !e.ctrlKey
            ? 'orbit'
            : 'pan';
    } else if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      this.lastPinchDist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
    } else if (this.pointers.size === 1) {
      // Double-tap detection for touch (dblclick is mouse-only on iOS).
      const now = performance.now();
      if (now - this.lastTapTime < 320) {
        this.onDoublePress?.(e.clientX, e.clientY);
      }
      this.lastTapTime = now;
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    const prev = this.pointers.get(e.pointerId);
    if (!prev) return;
    if (this.activeDrag) {
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pressStart && !this.pressStart.moved) {
        const total = Math.hypot(e.clientX - this.pressStart.x, e.clientY - this.pressStart.y);
        if (total > 5) this.pressStart.moved = true;
      }
      this.activeDrag.move(e);
      return;
    }
    const dx = e.clientX - prev.x;
    const dy = e.clientY - prev.y;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.pressStart && !this.pressStart.moved) {
      const total = Math.hypot(e.clientX - this.pressStart.x, e.clientY - this.pressStart.y);
      if (total > 5 || this.pointers.size > 1) this.pressStart.moved = true;
    }
    if (this.rightPress && !this.rightPress.moved) {
      if (Math.hypot(e.clientX - this.rightPress.x, e.clientY - this.rightPress.y) > 5) {
        this.rightPress.moved = true;
      }
    }

    if (e.pointerType === 'mouse') {
      if (this.mouseMode === 'orbit') this.orbit(dx, dy);
      else if (this.mouseMode === 'pan') this.pan(dx, dy);
      return;
    }

    // Touch
    if (this.pointers.size === 1) {
      this.orbit(dx, dy);
    } else if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      const dist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      const midX = (a!.x + b!.x) / 2;
      const midY = (a!.y + b!.y) / 2;
      if (this.lastPinchDist > 0 && dist > 0) {
        this.zoomAt(midX, midY, dist / this.lastPinchDist);
      }
      this.lastPinchDist = dist;
      // Two-finger drag pans (halved: each pointer contributes per move).
      this.pan(dx / 2, dy / 2);
    }
  };

  private onPointerUp = (e: PointerEvent): void => {
    this.pointers.delete(e.pointerId);
    this.lastPinchDist = 0;
    if (e.pointerType === 'mouse') this.mouseMode = null;
    if (this.activeDrag && this.pointers.size === 0) {
      const drag = this.activeDrag;
      this.activeDrag = null;
      drag.up(e);
    }
    if (this.pressStart && !this.pressStart.moved && this.pointers.size === 0) {
      this.onTap?.(e.clientX, e.clientY, this.pressStart.shift);
    }
    if (e.button === 2 && this.rightPress && !this.rightPress.moved) {
      this.onContextTap?.(e.clientX, e.clientY);
    }
    if (this.pointers.size === 0) {
      this.pressStart = null;
      this.rightPress = null;
    }
  };

  private onWheel = (e: WheelEvent): void => {
    if (this.isUiChrome(e)) return; // allow panel scrolling
    e.preventDefault();
    if (this.gestureActive) return; // Safari pinch handled by gesture events
    const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    // ctrl+wheel is how Chromium/Firefox report trackpad pinch — zoom harder.
    const speed = e.ctrlKey ? 0.008 : 0.0018;
    this.zoomAt(e.clientX, e.clientY, Math.exp(-delta * speed));
  };

  private onGestureStart = (e: WebKitGestureEvent): void => {
    e.preventDefault();
    this.gestureActive = true;
    this.lastGestureScale = e.scale;
  };

  private onGestureChange = (e: WebKitGestureEvent): void => {
    e.preventDefault();
    if (this.lastGestureScale > 0) {
      this.zoomAt(e.clientX, e.clientY, e.scale / this.lastGestureScale);
    }
    this.lastGestureScale = e.scale;
  };

  private onGestureEnd = (e: WebKitGestureEvent): void => {
    e.preventDefault();
    this.gestureActive = false;
  };

  private onDblClick = (e: MouseEvent): void => {
    if (this.isUiChrome(e)) return;
    this.onDoublePress?.(e.clientX, e.clientY);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'f' || e.key === 'F') {
      if (!(e.target instanceof HTMLInputElement)) this.onRequestFit?.();
    }
  };

  // --- primitive motions ----------------------------------------------------

  private orbit(dx: number, dy: number): void {
    const h = this.dom.clientHeight || 1;
    this.theta = (this.theta - (dx / h) * Math.PI) % TWO_PI;
    this.phi = Math.min(Math.PI - 0.02, Math.max(0.02, this.phi - (dy / h) * Math.PI));
    this.apply();
  }

  private pan(dx: number, dy: number): void {
    const h = this.dom.clientHeight || 1;
    const worldPerPixel =
      (2 * this.radius * Math.tan(((this.camera.fov / 2) * Math.PI) / 180)) / h;
    const viewDir = this.target.clone().sub(this.camera.position).normalize();
    const right = viewDir.clone().cross(this.camera.up).normalize();
    const up = right.clone().cross(viewDir);
    this.target
      .addScaledVector(right, -dx * worldPerPixel)
      .addScaledVector(up, dy * worldPerPixel);
    this.apply();
  }

  /** Dolly by `factor` (>1 zooms in) about the world point under the cursor. */
  private zoomAt(clientX: number, clientY: number, factor: number): void {
    const clamped = Math.min(4, Math.max(0.25, factor));
    const nextRadius = this.radius / clamped;
    if (nextRadius < 0.5 || nextRadius > 200_000) return;

    const pivot = this.pointOnTargetPlane(clientX, clientY);
    const k = 1 / clamped;
    // Scale the target about the pivot; the camera follows via spherical state.
    this.target.copy(pivot.clone().add(this.target.clone().sub(pivot).multiplyScalar(k)));
    this.radius = nextRadius;
    this.apply();
  }

  /** Cursor ray ∩ the view-perpendicular plane through the target. */
  private pointOnTargetPlane(clientX: number, clientY: number): THREE.Vector3 {
    const rect = this.dom.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.camera);
    const viewDir = this.target.clone().sub(this.camera.position).normalize();
    const denom = ray.ray.direction.dot(viewDir);
    if (Math.abs(denom) < 1e-6) return this.target.clone();
    const t = this.target.clone().sub(ray.ray.origin).dot(viewDir) / denom;
    return ray.ray.origin.clone().addScaledVector(ray.ray.direction, t);
  }
}
